import { ShieldCheck, TrendingUp, CircleDashed } from "lucide-react";
import { cn } from "@/lib/utils";

export type LeadScoreBand = "STRONG" | "PROMISING" | "LOW_SIGNAL" | null;

export interface LeadBandChipProps {
  band: LeadScoreBand;
}

const BAND_CONFIG: Record<
  NonNullable<LeadScoreBand>,
  { label: string; icon: typeof ShieldCheck; className: string }
> = {
  STRONG: {
    label: "Strong lead",
    icon: ShieldCheck,
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  PROMISING: {
    label: "Promising",
    icon: TrendingUp,
    className: "border-sky-200 bg-sky-50 text-sky-700",
  },
  LOW_SIGNAL: {
    label: "Low signal",
    icon: CircleDashed,
    className: "border-slate-300 bg-slate-100 text-slate-600",
  },
};

/**
 * Band chip for a thread's AI-derived lead-quality score. Icon + text label —
 * never color-only. Returns null when `band` is null (not yet scored) — callers
 * should not render the chip at all in that case.
 */
export function LeadBandChip({ band }: LeadBandChipProps) {
  if (!band) return null;
  const config = BAND_CONFIG[band];
  const Icon = config.icon;

  return (
    <span
      role="status"
      data-testid="lead-band-chip"
      aria-label={`Lead quality: ${config.label}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold motion-safe:transition-colors motion-safe:duration-200",
        config.className,
      )}
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      {config.label}
    </span>
  );
}

export default LeadBandChip;
